import { useLayoutEffect, useState, type RefObject } from "react";

export type WireTone = "write" | "derive" | "read" | "mcp" | "db";

export type WireLink = {
  id: string;
  from: string;
  to: string;
  tone: WireTone;
  ambient?: boolean;
};

export type WireStory = {
  sourceId: string;
  feedVia: "db" | "api" | "mcp";
  /** Heavy Act I write animation */
  feedFocus: boolean;
  /** Chat reaches live systems via in-hub OpenAPI MCP */
  mcpLive: boolean;
  /** Chat reaches external DBs via in-hub DB Adaptors */
  dbLive: boolean;
  /** answer path emphasis */
  path: "os" | "dashboard" | "share" | "mcp" | "db";
};

type Pt = { x: number; y: number };
type Drawn = WireLink & { d: string; length: number };

function rectCenter(r: DOMRect) {
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/** Prefer work-icon / iso port so hub arrows attach to the animated badge. */
function resolveWireRect(root: HTMLElement, wireId: string): DOMRect | null {
  const el = root.querySelector(`[data-wire="${wireId}"]`);
  if (!el) return null;
  const port =
    el.querySelector(".gar-iso__busy") ||
    el.querySelector("[data-wire-port]") ||
    el.querySelector(".gar-iso__port");
  if (port) return port.getBoundingClientRect();
  return el.getBoundingClientRect();
}

function edgePoints(from: DOMRect, to: DOMRect, root: DOMRect): { a: Pt; b: Pt; c1: Pt; c2: Pt } {
  const ac = rectCenter(from);
  const bc = rectCenter(to);
  const dx = bc.x - ac.x;
  const dy = bc.y - ac.y;
  const portLink = from.width < 48 || to.width < 48 || from.height < 48 || to.height < 48;

  // Small work-icon ports: connect center-to-center into the badge
  if (portLink) {
    const a = { x: ac.x - root.left, y: ac.y - root.top };
    const b = { x: bc.x - root.left, y: bc.y - root.top };
    const bend = Math.max(24, Math.hypot(b.x - a.x, b.y - a.y) * 0.35);
    const horizontal = Math.abs(dx) >= Math.abs(dy) * 0.85;
    if (horizontal) {
      const dir = b.x >= a.x ? 1 : -1;
      return {
        a,
        b,
        c1: { x: a.x + bend * dir, y: a.y },
        c2: { x: b.x - bend * dir, y: b.y },
      };
    }
    const dir = b.y >= a.y ? 1 : -1;
    return {
      a,
      b,
      c1: { x: a.x, y: a.y + bend * dir },
      c2: { x: b.x, y: b.y - bend * dir },
    };
  }

  const horizontal = Math.abs(dx) >= Math.abs(dy) * 0.85;
  let a: Pt;
  let b: Pt;

  if (horizontal) {
    if (dx >= 0) {
      a = { x: from.right - root.left, y: ac.y - root.top };
      b = { x: to.left - root.left, y: bc.y - root.top };
    } else {
      a = { x: from.left - root.left, y: ac.y - root.top };
      b = { x: to.right - root.left, y: bc.y - root.top };
    }
    const bend = Math.max(36, Math.abs(b.x - a.x) * 0.42);
    const dir = b.x >= a.x ? 1 : -1;
    return {
      a,
      b,
      c1: { x: a.x + bend * dir, y: a.y },
      c2: { x: b.x - bend * dir, y: b.y },
    };
  }

  if (dy >= 0) {
    a = { x: ac.x - root.left, y: from.bottom - root.top };
    b = { x: bc.x - root.left, y: to.top - root.top };
  } else {
    a = { x: ac.x - root.left, y: from.top - root.top };
    b = { x: bc.x - root.left, y: to.bottom - root.top };
  }
  const bend = Math.max(28, Math.abs(b.y - a.y) * 0.42);
  const dir = b.y >= a.y ? 1 : -1;
  return {
    a,
    b,
    c1: { x: a.x, y: a.y + bend * dir },
    c2: { x: b.x, y: b.y - bend * dir },
  };
}

function pathFromPoints(p: { a: Pt; b: Pt; c1: Pt; c2: Pt }) {
  return `M ${p.a.x.toFixed(1)} ${p.a.y.toFixed(1)} C ${p.c1.x.toFixed(1)} ${p.c1.y.toFixed(1)}, ${p.c2.x.toFixed(1)} ${p.c2.y.toFixed(1)}, ${p.b.x.toFixed(1)} ${p.b.y.toFixed(1)}`;
}

function approxLength(p: { a: Pt; b: Pt; c1: Pt; c2: Pt }) {
  return (
    Math.hypot(p.c1.x - p.a.x, p.c1.y - p.a.y) +
    Math.hypot(p.c2.x - p.c1.x, p.c2.y - p.c1.y) +
    Math.hypot(p.b.x - p.c2.x, p.b.y - p.c2.y)
  );
}

type Props = {
  containerRef: RefObject<HTMLElement | null>;
  links: WireLink[];
  active: boolean;
  revision: string | number;
};

export default function FlowWires({ containerRef, links, active, revision }: Props) {
  const [drawn, setDrawn] = useState<Drawn[]>([]);
  const [size, setSize] = useState({ w: 1, h: 1 });

  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    const measure = () => {
      const rootRect = root.getBoundingClientRect();
      setSize({ w: Math.max(1, rootRect.width), h: Math.max(1, rootRect.height) });

      const next: Drawn[] = [];
      for (const link of links) {
        const from = resolveWireRect(root, link.from);
        const to = resolveWireRect(root, link.to);
        if (!from || !to) continue;
        if (from.width < 1 || to.width < 1) continue;
        const pts = edgePoints(from, to, rootRect);
        next.push({ ...link, d: pathFromPoints(pts), length: approxLength(pts) });
      }
      setDrawn(next);
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(root);
    window.addEventListener("resize", measure);
    const t = window.setTimeout(measure, 50);
    const t2 = window.setTimeout(measure, 320);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, [containerRef, links, revision, active]);

  if (!active || drawn.length === 0) return null;

  const toneFill = (tone: WireTone) =>
    tone === "write"
      ? "#f0c36a"
      : tone === "derive"
        ? "#a78bfa"
        : tone === "mcp"
          ? "#c4b5fd"
          : tone === "db"
            ? "#60a5fa"
            : "#5ec8ff";

  return (
    <svg
      className="gar-game__wires"
      width={size.w}
      height={size.h}
      viewBox={`0 0 ${size.w} ${size.h}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        {(["write", "derive", "read", "mcp", "db"] as WireTone[]).map((tone) => (
          <marker
            key={tone}
            id={`gar-arrow-${tone}`}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={toneFill(tone)} />
          </marker>
        ))}
      </defs>

      {drawn.map((w) => (
        <g key={w.id}>
          <path
            className={`gar-wire gar-wire--on gar-wire--${w.tone}${w.ambient ? " gar-wire--ambient" : ""}`}
            d={w.d}
            markerEnd={w.ambient ? undefined : `url(#gar-arrow-${w.tone})`}
            pathLength={100}
          />
          <circle
            className={`gar-orb gar-orb--fly gar-orb--${w.tone}${w.ambient ? " gar-orb--ambient" : ""}`}
            r={w.ambient ? 3.5 : 5}
            fill={toneFill(w.tone)}
          >
            <animateMotion
              dur={`${Math.min(4.8, Math.max(2.2, w.length / (w.ambient ? 140 : 180)))}s`}
              repeatCount="indefinite"
              path={w.d}
            />
          </circle>
        </g>
      ))}
    </svg>
  );
}

/** Source → (in-hub DB Adaptors | API/MCP gate) → Prep/Ingest/Index → Connect AI DB (+ Excel/CSV via Service API). */
export function writeFeedWires(
  sourceId: string,
  feedVia: "db" | "api" | "mcp",
  ambient: boolean
): WireLink[] {
  const intoJobs: WireLink[] =
    feedVia === "db"
      ? [
          { id: "w1", from: `src-${sourceId}`, to: "hub-db", tone: "write", ambient },
          { id: "w1b", from: "hub-db", to: "gate-db", tone: "write", ambient: true },
          { id: "w2", from: "hub-db", to: "hub-prep", tone: "write", ambient },
        ]
      : [
          { id: "w1", from: `src-${sourceId}`, to: `gate-${feedVia}`, tone: "write", ambient },
          { id: "w2", from: `gate-${feedVia}`, to: "hub-prep", tone: "write", ambient },
        ];

  return [
    ...intoJobs,
    { id: "w3", from: "hub-prep", to: "hub-ingest", tone: "write", ambient },
    // Manual files → Service API → Ingest (never direct file → ingest)
    { id: "wf1", from: "file-manual", to: "service-api", tone: "write", ambient },
    { id: "wf2", from: "service-api", to: "hub-ingest", tone: "write", ambient },
    { id: "w4", from: "hub-ingest", to: "hub-index", tone: "write", ambient },
    { id: "w5", from: "hub-index", to: "hub-opensearch", tone: "write", ambient },
  ];
}

/**
 * In-hub OpenAPI MCP uses registered OpenAPI catalogs to call external HTTP APIs
 * (not a direct skip from hub to source without the catalog layer).
 */
export function mcpOutboundWires(sourceId: string, ambient: boolean): WireLink[] {
  return [
    { id: "m1", from: "hub-router", to: "hub-mcp", tone: "mcp", ambient },
    { id: "m2", from: "hub-mcp", to: "gate-mcp", tone: "mcp", ambient },
    { id: "m3", from: "gate-mcp", to: "ext-api", tone: "mcp", ambient },
    { id: "m4", from: "hub-mcp", to: "ext-api", tone: "mcp", ambient: true },
    { id: "m5", from: "ext-api", to: `src-${sourceId}`, tone: "mcp", ambient },
  ];
}

/** In-hub DB Adaptors pull live rows from external system databases via the resource router. */
export function dbOutboundWires(sourceId: string, ambient: boolean): WireLink[] {
  return [
    { id: "db1", from: "hub-router", to: "hub-db", tone: "db", ambient },
    { id: "db2", from: "hub-db", to: "gate-db", tone: "db", ambient },
    { id: "db3", from: "hub-db", to: `src-${sourceId}`, tone: "db", ambient },
    { id: "db4", from: "gate-db", to: `src-${sourceId}`, tone: "db", ambient: true },
  ];
}

/**
 * Responses never jump hub → Outcome. Always hub module → Chat API → Outcome.
 */
export function apiReturnWires(
  fromHub: string,
  opts?: { ambient?: boolean; idPrefix?: string }
): WireLink[] {
  const p = opts?.idPrefix ?? "ret";
  const ambient = opts?.ambient ?? false;
  return [
    { id: `${p}1`, from: fromHub, to: "chat-api", tone: "read", ambient },
    { id: `${p}2`, from: "chat-api", to: "outcome", tone: "read", ambient },
  ];
}

export function wiresForPhase(phase: string, story: WireStory): WireLink[] {
  const write = writeFeedWires(story.sourceId, story.feedVia, !story.feedFocus || phase !== "feed");

  switch (phase) {
    case "feed":
      return writeFeedWires(story.sourceId, story.feedVia, false);
    case "derive":
      return [
        ...writeFeedWires(story.sourceId, story.feedVia, true),
        { id: "d1", from: "hub-opensearch", to: "hub-guide", tone: "derive" },
        { id: "d2", from: "hub-index", to: "hub-guide", tone: "derive" },
        { id: "d3", from: "hub-guide", to: "hub-router", tone: "derive" },
        ...(story.feedVia === "mcp" || story.mcpLive
          ? [{ id: "d4", from: "hub-mcp", to: "hub-guide", tone: "derive" as const }]
          : []),
      ];
    case "typing":
    case "shrink":
      return write;
    case "launch":
      return [
        ...write,
        { id: "l1", from: "ask", to: "chat-api", tone: "read" },
        { id: "l2", from: "chat-api", to: "hub-chat", tone: "read" },
      ];
    case "bind": {
      const base: WireLink[] = [
        ...write,
        { id: "b0", from: "ask", to: "chat-api", tone: "read", ambient: true },
        { id: "b1", from: "chat-api", to: "hub-chat", tone: "read" },
        { id: "b2", from: "hub-chat", to: "hub-router", tone: "read" },
      ];
      if (story.mcpLive) {
        return [
          ...base,
          { id: "b3", from: "hub-router", to: "hub-opensearch", tone: "read", ambient: true },
          ...mcpOutboundWires(story.sourceId, false),
        ];
      }
      if (story.dbLive || story.path === "db") {
        return [
          ...base,
          { id: "b3", from: "hub-router", to: "hub-opensearch", tone: "read", ambient: true },
          { id: "b4", from: "hub-router", to: "hub-guide", tone: "read", ambient: true },
          ...dbOutboundWires(story.sourceId, false),
        ];
      }
      return [
        ...base,
        { id: "b3", from: "hub-router", to: "hub-opensearch", tone: "read" },
        { id: "b4", from: "hub-router", to: "hub-guide", tone: "read" },
      ];
    }
    case "answer": {
      if (story.path === "mcp" || story.mcpLive) {
        return [
          ...write,
          ...mcpOutboundWires(story.sourceId, false),
          { id: "a0", from: "ext-api", to: "hub-mcp", tone: "mcp" },
          { id: "a1", from: "hub-mcp", to: "hub-chat", tone: "mcp" },
          { id: "a2", from: "hub-guide", to: "hub-chat", tone: "read", ambient: true },
          ...apiReturnWires("hub-chat"),
        ];
      }
      if (story.path === "db" || story.dbLive) {
        return [
          ...write,
          ...dbOutboundWires(story.sourceId, false),
          { id: "a1", from: "hub-db", to: "hub-chat", tone: "db" },
          { id: "a2", from: "hub-guide", to: "hub-chat", tone: "read", ambient: true },
          ...apiReturnWires("hub-chat"),
        ];
      }
      if (story.path === "dashboard") {
        return [
          ...write,
          { id: "a1", from: "hub-opensearch", to: "hub-dashboards", tone: "read" },
          { id: "a2", from: "hub-chat", to: "hub-dashboards", tone: "read" },
          ...apiReturnWires("hub-dashboards"),
        ];
      }
      if (story.path === "share") {
        return [
          ...write,
          { id: "a1", from: "hub-dashboards", to: "hub-share", tone: "read" },
          { id: "a2", from: "hub-opensearch", to: "hub-share", tone: "read", ambient: true },
          ...apiReturnWires("hub-share"),
        ];
      }
      return [
        ...write,
        { id: "a1", from: "hub-opensearch", to: "hub-chat", tone: "read" },
        { id: "a2", from: "hub-guide", to: "hub-chat", tone: "read" },
        { id: "a3", from: "hub-chat", to: "hub-dashboards", tone: "read", ambient: true },
        ...apiReturnWires("hub-chat"),
        ...apiReturnWires("hub-dashboards", { ambient: true, idPrefix: "retd" }),
      ];
    }
    case "reveal":
    case "hold":
      if (story.path === "share") {
        return [...write, ...apiReturnWires("hub-share")];
      }
      if (story.path === "dashboard") {
        return [...write, ...apiReturnWires("hub-dashboards")];
      }
      return [
        ...write,
        ...apiReturnWires("hub-chat"),
        ...apiReturnWires("hub-dashboards", { ambient: true, idPrefix: "retd" }),
      ];
    default:
      return write;
  }
}
